import React, { FC, useContext, useMemo } from 'react';
import { useHistory } from 'react-router-dom';

import { useFormikContext } from 'formik';

import AampeToggleButton from '../../../../components/shared/AampeToggleButton/AampeToggleButton';
import styled from 'styled-components';

import aampeTheme from '../../../../constants/aampeTheme';
import { FormulaTestMessageType } from '../../../../constants/formula';
import routes from '../../../../constants/routes';

import { EditorContext } from '../../../../context/EditorContext';

import usePushIntegrationQuery from '../../../../hooks/pushIntegrations/usePushIntegrationsQuery';

import { FormulaTestMessageFormValues } from 'models/formula.model';

import TestFormulaAttributeFields from './TestFormulaAttributeFields';
import TestFormulaGroupUserForm from './TestFormulaGroupUserForm';
import TestFormulaOneUserForm from './TestFormulaOneUserForm';

interface TestFormulaFormMainProps {
    handleNeedHelp: () => void;
    isLoading: boolean;
    menuPortalId?: string;
    handleClose?: () => void;
    isModalView?: boolean;
}

const TestFormulaFormMainV1: FC<TestFormulaFormMainProps> = ({
    handleNeedHelp,
    isLoading,
    menuPortalId,
    handleClose,
    isModalView = false,
}) => {
    const {
        editorDetails: { formulaType },
    } = useContext(EditorContext);

    const { setFieldValue, values } =
        useFormikContext<FormulaTestMessageFormValues>();

    const { data: pushIntegrationData } = usePushIntegrationQuery();

    const history = useHistory();

    const matchedIntegration = useMemo(() => {
        if (pushIntegrationData && formulaType) {
            return pushIntegrationData.find((integration) =>
                integration.channels?.includes(formulaType),
            );
        }
    }, [pushIntegrationData, formulaType]);

    const providerId = matchedIntegration?.id;

    const resetUserFilters = () => {
        setFieldValue('contact_ids', []);
        setFieldValue('notification_token', undefined);
    };

    return (
        <>
            <StyledAampeToggleButton
                buttons={[
                    {
                        title: 'One user',
                        value: FormulaTestMessageType.OneUser,
                        isLeft: true,
                        isSelected: values.current_state,
                        onChange: () => {
                            setFieldValue(
                                'current_state',
                                FormulaTestMessageType.OneUser,
                            );
                            resetUserFilters();
                        },
                    },

                    {
                        title: 'User group',
                        value: FormulaTestMessageType.GroupUser,
                        isRight: true,
                        isSelected: values.current_state,
                        onChange: () => {
                            setFieldValue(
                                'current_state',
                                FormulaTestMessageType.GroupUser,
                            );
                            resetUserFilters();
                        },
                    },
                ]}
                selectedColor={aampeTheme.colors.neutral09}
            />

            {values.current_state === FormulaTestMessageType.OneUser && (
                <TestFormulaOneUserForm
                    isLoading={isLoading}
                    menuPortalId={menuPortalId}
                    handleClose={handleClose}
                    isModalView={isModalView}
                    providerId={providerId}
                    handleNeedHelp={handleNeedHelp}
                />
            )}
            {values.current_state === FormulaTestMessageType.GroupUser && (
                <TestFormulaGroupUserForm
                    providerId={providerId}
                    handleCreateGroup={() =>
                        history.push(routes.testMessageSettings)
                    }
                    providerAdditionalAttributes={
                        matchedIntegration?.additional_provider_attributes ?? {}
                    }
                />
            )}
            {values.current_state === FormulaTestMessageType.OneUser &&
                values.contact_ids[0] && (
                    <TestFormulaAttributeFields
                        contactId={values.contact_ids[0]}
                        providerAdditionalAttributes={
                            matchedIntegration?.additional_provider_attributes ??
                            {}
                        }
                    />
                )}
        </>
    );
};

const StyledAampeToggleButton = styled(AampeToggleButton)({
    width: '100%',
});

export default TestFormulaFormMainV1;
